'use client';
import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { elowenClient } from "../elowenClient";
import { QUERY_KEYS } from "../queries";
import { configConflictSnapshot } from "../configRevisionConflict";
import type { SystemUpdateStatus } from "../types";
import type { ConfigPatch, ConfigSnapshot } from "../types";

export function useUpdateConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: ConfigPatch) => {
      const current = qc.getQueryData<ConfigSnapshot>(QUERY_KEYS.config);
      return elowenClient.updateConfig(patch, current?.revision);
    },
    onSuccess: (snapshot, patch) => {
      qc.setQueryData(QUERY_KEYS.config, snapshot);
      if (patch.voice || patch.brain?.providers) void qc.invalidateQueries({ queryKey: QUERY_KEYS.voiceCatalog });
      if (patch.brain?.defaultModel || patch.brain?.providers) {
        void qc.invalidateQueries({ queryKey: ['brain-models'] });
        void qc.invalidateQueries({ queryKey: ['grantable-brain-models'] });
        void qc.invalidateQueries({ queryKey: ['my-cli-settings'] });
      }
    },
    onError: (error) => adoptConfigConflict(qc, error),
  });
}

/** A config conflict is a successful read of newer canonical state. Keep the draft in its owner, but advance
 *  the shared cache so an explicit Retry, or the next autosave, uses the current revision instead of repeating
 *  the same 409. Every `PUT /config` mutation answers its errors through this. */
export function adoptConfigConflict(qc: QueryClient, error: unknown): void {
  const current = configConflictSnapshot(error);
  if (current) qc.setQueryData(QUERY_KEYS.config, current);
}

/** The explicit replace of an unreadable settings row. The answer is the readable snapshot of the defaults,
 *  so the shared config cache is written from it and every model list that depends on providers is asked again. */
export function useResetUnreadableConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => elowenClient.resetUnreadableConfig(),
    onSuccess: (snapshot) => {
      qc.setQueryData(QUERY_KEYS.config, snapshot);
      void qc.invalidateQueries({ queryKey: ['brain-models'] });
      void qc.invalidateQueries({ queryKey: ['grantable-brain-models'] });
      void qc.invalidateQueries({ queryKey: ['my-cli-settings'] });
    },
  });
}

/** Preserve the accepted request in the shared status cache until its canonical status is fetched.
 *  This is a queued hint only, never an optimistic installed version or enablement change. */
export function acceptedPluginUpdates(qc: ReturnType<typeof useQueryClient>, plugins: SystemUpdateStatus['queue']['plugins']) {
  qc.setQueryData<SystemUpdateStatus>(QUERY_KEYS.systemUpdate, current => {
    const status = current ?? { queue: { schema: 1 as const, core: null, plugins: [] }, journal: null, report: null, consentPlans: [] };
    return { ...status, queue: { ...status.queue, plugins: [
      ...status.queue.plugins.filter(op => !plugins.some(accepted => accepted.name === op.name)), ...plugins,
    ] } };
  });
  void qc.invalidateQueries({ queryKey: QUERY_KEYS.systemUpdate });
}

export function acceptedUpdate(qc: ReturnType<typeof useQueryClient>, requestId: string, op?: { name: string; op: 'install' | 'update' | 'repair'; enable: boolean }) {
  if (op) {
    acceptedPluginUpdates(qc, [{ ...op, requestId, requestedAt: Date.now(), by: null, pin: null }]);
    return;
  }
  qc.setQueryData<SystemUpdateStatus>(QUERY_KEYS.systemUpdate, current => {
    const status = current ?? { queue: { schema: 1 as const, core: null, plugins: [] }, journal: null, report: null, consentPlans: [] };
    return { ...status, queue: { ...status.queue, core: { requestId, requestedAt: Date.now(), by: null } } };
  });
  void qc.invalidateQueries({ queryKey: QUERY_KEYS.systemUpdate });
}

/** Queue a whole-instance update. Its proof and report, not this acknowledgement, determine success. */
export function useSystemUpdate() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: () => elowenClient.systemUpdate(), onSuccess: result => acceptedUpdate(qc, result.requestId) });
}

export function useUpdateConsentDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { plan: SystemUpdateStatus['consentPlans'][number]; action: 'confirm' | 'decline' }) => {
      if (input.action === 'confirm') await elowenClient.confirmUpdateConsent(input.plan);
      else await elowenClient.declineUpdateConsent(input.plan.id);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: QUERY_KEYS.systemUpdate }),
  });
}

/** Restart one of the systemd units. No invalidation — a daemon restart drops the API for a few
 *  seconds anyway; the System panel's regular polling picks the service back up on its own. */
export function useSystemRestart() {
  return useMutation({ mutationFn: (target: 'daemon' | 'web') => elowenClient.systemRestart(target) });
}

/** Delete one log file. Both the list and any cached read of that file are dropped — the viewer must not
 *  keep showing the contents of a file that is gone. */
export function useDeleteLogFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => elowenClient.deleteLogFile(name),
    onSuccess: (_d, name) => {
      void qc.invalidateQueries({ queryKey: ['log-files'] });
      void qc.removeQueries({ queryKey: ['log-file', name] });
    },
  });
}

/** Delete every log file. Drops every cached read, since none of them still exist. */
export function useDeleteAllLogFiles() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => elowenClient.deleteAllLogFiles(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['log-files'] });
      void qc.removeQueries({ queryKey: ['log-file'] });
    },
  });
}
